import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pb } from '../lib/pb';
import { errorMessage } from '../lib/errors';
import { formatDate } from '../i18n';
import { toast, useStore } from '../store/store';
import { loadGameData, setData } from '../data/dataStore';
import type { Changeset, DataExportRecord } from '../data/types';

interface Preview {
  changeset: Changeset;
  summary: string;
  count: number;
}

/** Admin → Export to Unity: approved changes → a change package for EnyaRPG → Maps Editor → Apply change package…. */
export function DataExportAdmin() {
  const { t } = useTranslation();
  const users = useStore((s) => s.users);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [exports, setExports] = useState<DataExportRecord[]>([]);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [p, list] = await Promise.all([
        pb.send<Preview>('/api/gamedata/export', {}),
        pb.collection('data_exports').getList<DataExportRecord>(1, 20, { sort: '-created' }),
      ]);
      setPreview(p);
      setExports(list.items);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }, []);

  useEffect(() => {
    // Loading from the server, then setting state when it arrives, is what this effect is for.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh();
  }, [refresh]);

  const create = async () => {
    if (!confirm(t('admin.dataexport.confirm', { count: preview?.count ?? 0 }))) return;
    setBusy(true);
    try {
      const res = await pb.send<{ id: string; changeset: Changeset; summary: string }>(
        '/api/gamedata/export',
        {
          method: 'POST',
          body: {},
        },
      );
      download(res.changeset, res.summary);
      toast(t('admin.dataexport.done'));
      await Promise.all([refresh(), loadGameData(true)]);
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-export">
      <p className="muted small">{t('admin.dataexport.help')}</p>
      {!preview ? (
        <p className="muted">{t('common.loading')}</p>
      ) : preview.count === 0 ? (
        <p className="muted">{t('admin.dataexport.nothing')}</p>
      ) : (
        <>
          {!!preview.changeset.warnings?.length && (
            <div className="banner warn">
              {preview.changeset.warnings.map((w, i) => (
                <span key={i}>{w}</span>
              ))}
            </div>
          )}
          <ul className="export-list">
            {preview.changeset.changes.map((c) => (
              <li key={c.recordId} className="card">
                <span className="row">
                  {c.op === 'create' && <span className="chip new">{t('data.create.newBadge')}</span>}
                  <button
                    className="link"
                    onClick={() => setData({ selectedId: c.recordId, category: c.category })}
                  >
                    {c.name}
                  </button>
                </span>
                <span className="muted small">
                  {t(`data.categories.${c.category}`, { defaultValue: c.categoryLabel })}
                  {c.proposedBy && ` · ${c.proposedBy}`}
                </span>
                {c.op === 'create' && (
                  <span className="muted small">
                    {t('admin.dataexport.copyOf', { name: c.templateName })} →{' '}
                    <span className="mono">{c.path}</span>
                  </span>
                )}
                {c.progressShift && (
                  <span className="small warn-text">
                    ⚠ {t('admin.dataexport.progressWarning', { where: c.progressShift.join(', ') })}
                  </span>
                )}
                <ul className="small">
                  {c.fields.flatMap((f) =>
                    f.details.map((d, i) => (
                      <li key={f.field + i}>
                        <strong>{d.path}</strong>
                        {d.op === 'added' && (
                          <>
                            {' '}
                            {t('admin.dataexport.added')}: {d.to}
                          </>
                        )}
                        {d.op === 'removed' && (
                          <>
                            {' '}
                            {t('admin.dataexport.removed')} <span className="muted">({d.from})</span>
                          </>
                        )}
                        {d.op === 'changed' && (
                          <>
                            : {d.from} → {d.to}
                          </>
                        )}
                        {f.field === c.keyField && c.op !== 'create' && (
                          <span className="warn-text"> ⚠ {t('admin.dataexport.keyWarning')}</span>
                        )}
                      </li>
                    )),
                  )}
                </ul>
                {c.note && <div className="small muted">✎ {c.note}</div>}
                {c.comments.length > 0 && (
                  <div className="small muted">
                    {t('admin.dataexport.comments', { count: c.comments.length })}
                  </div>
                )}
              </li>
            ))}
          </ul>
          <button className="primary" disabled={busy} onClick={() => void create()}>
            {t('admin.dataexport.create', { count: preview.count })}
          </button>
        </>
      )}
      {exports.length > 0 && (
        <>
          <h4 className="group-label">{t('admin.dataexport.earlier')}</h4>
          <table className="vertices small">
            <tbody>
              {exports.map((x) => (
                <tr key={x.id}>
                  <td>{formatDate(x.created)}</td>
                  <td>{users[x.by]?.name ?? '—'}</td>
                  <td>{t('admin.dataexport.assets', { count: x.count })}</td>
                  <td>
                    <button className="link small" onClick={() => download(x.changeset, x.summary)}>
                      {t('admin.dataexport.download')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

/** Saves the package for Unity (.json) and the review summary (.md) side by side. */
function download(changeset: Changeset, summary: string) {
  const stamp = changeset.createdAt.replace(/[-:]/g, '').replace('T', '-').slice(0, 13);
  save(`changeset-${stamp}.json`, JSON.stringify(changeset, null, 2), 'application/json');
  save(`changeset-${stamp}.md`, summary, 'text/markdown');
}

function save(name: string, text: string, type: string) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
